import { useContext } from "react";
import { DialogContext, type DialogContextValue } from "~/contexts/dialogContext";

/**
 * Hook to access dialog system from context
 *
 * Must be used within a DialogProvider.
 *
 * Example:
 * ```tsx
 * const { openDialog, closeDialog } = useDialog();
 * 
 * // Confirm dialog
 * openDialog({
 *   type: 'confirm',
 *   title: 'Delete Item',
 *   message: 'Are you sure?',
 *   onConfirm: () => deleteItem(id),
 * });
 * 
 * // Input dialog
 * openDialog({
 *   type: 'input',
 *   title: 'Take Damage',
 *   inputLabel: 'Amount',
 *   onConfirm: (amount) => applyDamage(amount),
 * });
 * ```
 *
 * @returns Dialog context value
 * @throws Error if used outside of DialogProvider
 */
export const useDialog = (): DialogContextValue => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return context;
};
